/**
 * Resident Life Section — Video Player
 *
 * Supports 3 modes per section:
 *   1. Uploaded file (<video>) via data-video-src
 *   2. YouTube/Vimeo embed via data-video-embed
 *   3. Poster only (no playable source)
 *
 * On click of .block-resident-life-section__play:
 *   - Swaps the poster for a <video> or <iframe> with autoplay
 *   - Hides the play button and poster
 */

export const ResidentLifeVideo = {
	init() {
		document
			.querySelectorAll( '.block-resident-life-section__video' )
			.forEach( ( wrap ) => this.initBlock( wrap ) );
	},

	initBlock( wrap ) {
		const src = wrap.dataset.videoSrc || '';
		const embed = wrap.dataset.videoEmbed || '';
		const btn = wrap.querySelector( '.block-resident-life-section__play' );

		if ( ! btn || ( ! src && ! embed ) ) {
			// No playable source — leave poster as a static image.
			if ( btn && ! src && ! embed ) {
				btn.style.display = 'none';
			}
			return;
		}

		btn.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			if ( wrap.classList.contains( 'is-playing' ) ) {
				return;
			}
			wrap.classList.add( 'is-playing' );

			const el = src
				? this.buildVideoEl( src )
				: this.buildEmbedEl( embed );

			if ( ! el ) {
				wrap.classList.remove( 'is-playing' );
				return;
			}

			wrap.appendChild( el );
		} );
	},

	buildVideoEl( src ) {
		const v = document.createElement( 'video' );
		v.src = src;
		v.controls = true;
		v.autoplay = true;
		v.playsInline = true;
		v.className = 'block-resident-life-section__video-player';
		return v;
	},

	buildEmbedEl( url ) {
		const info = this.parseEmbed( url );
		if ( ! info ) {
			return null;
		}
		const iframe = document.createElement( 'iframe' );
		iframe.src = info.src;
		iframe.title = 'Video player';
		iframe.loading = 'lazy';
		iframe.allow =
			'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
		iframe.allowFullscreen = true;
		iframe.className = 'block-resident-life-section__video-player';
		return iframe;
	},

	parseEmbed( url ) {
		try {
			const u = new URL( url );
			const host = u.hostname.replace( /^www\./, '' );

			// YouTube
			if ( host === 'youtube.com' || host === 'm.youtube.com' ) {
				const id = u.searchParams.get( 'v' );
				if ( id ) {
					return { src: `https://www.youtube.com/embed/${ id }?autoplay=1&rel=0` };
				}
				// /embed/ID already
				const m = u.pathname.match( /\/embed\/([\w-]+)/ );
				if ( m ) {
					return { src: `https://www.youtube.com/embed/${ m[ 1 ] }?autoplay=1&rel=0` };
				}
			}
			if ( host === 'youtu.be' ) {
				const id = u.pathname.replace( /^\//, '' );
				if ( id ) {
					return { src: `https://www.youtube.com/embed/${ id }?autoplay=1&rel=0` };
				}
			}

			// Vimeo
			if ( host === 'vimeo.com' || host === 'player.vimeo.com' ) {
				const m = u.pathname.match( /(\d+)/ );
				if ( m ) {
					return { src: `https://player.vimeo.com/video/${ m[ 1 ] }?autoplay=1` };
				}
			}
		} catch ( e ) {
			return null;
		}
		return null;
	},
};
